import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { webinarAPI } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Save, Eye, Calendar, Users, Lock, Globe, DollarSign, Settings2, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { AppLayout } from '@/components/layout/AppLayout';

export default function WebinarEditorPage() {
  const { webinarId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEdit = !!webinarId;

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: '',
    tags: '',
    visibility: 'public',
    banner_url: '',
    thumbnail_url: '',
    pricing_type: 'free',
    pricing_price: 0,
    schedule_start: '',
    schedule_duration: 60,
    schedule_timezone: 'Europe/Paris',
    max_participants: 100,
    mode: 'live',
    allow_chat: true,
    allow_reactions: true,
    allow_hand_raise: true,
    allow_qna: true,
    allow_polls: true,
    allow_screen_share: true,
    allow_whiteboard: true,
    enable_recording: true,
    enable_breakout_rooms: false,
    require_approval: false,
    waiting_room: false,
    course_id: '',
  });

  useEffect(() => {
    if (isEdit) {
      webinarAPI.get(webinarId).then(({ data }) => {
        const s = data.schedule || {};
        const p = data.pricing || {};
        const st = data.settings || {};
        setForm({
          title: data.title || '',
          description: data.description || '',
          category: data.category || '',
          tags: (data.tags || []).join(', '),
          visibility: data.visibility || 'public',
          banner_url: data.banner_url || '',
          thumbnail_url: data.thumbnail_url || '',
          pricing_type: p.type || 'free',
          pricing_price: p.price || 0,
          schedule_start: s.start_at ? s.start_at.slice(0, 16) : '',
          schedule_duration: s.duration_minutes || 60,
          schedule_timezone: s.timezone || 'Europe/Paris',
          max_participants: st.max_participants || 100,
          mode: st.mode || 'live',
          allow_chat: st.allow_chat !== false,
          allow_reactions: st.allow_reactions !== false,
          allow_hand_raise: st.allow_hand_raise !== false,
          allow_qna: st.allow_qna !== false,
          allow_polls: st.allow_polls !== false,
          allow_screen_share: st.allow_screen_share !== false,
          allow_whiteboard: st.allow_whiteboard !== false,
          enable_recording: st.enable_recording !== false,
          enable_breakout_rooms: st.enable_breakout_rooms === true,
          require_approval: st.require_approval === true,
          waiting_room: st.waiting_room === true,
          course_id: data.course_id || '',
        });
      }).catch(() => toast.error('Webinaire introuvable'));
    }
  }, [isEdit, webinarId]);

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Le titre est requis'); return; }
    if (!form.schedule_start) { toast.error('La date de début est requise'); return; }

    const payload = {
      title: form.title,
      description: form.description,
      category: form.category || null,
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      visibility: form.visibility,
      banner_url: form.banner_url || null,
      thumbnail_url: form.thumbnail_url || null,
      pricing: { type: form.pricing_type, price: Number(form.pricing_price), currency: 'EUR' },
      schedule: {
        start_at: new Date(form.schedule_start).toISOString(),
        duration_minutes: Number(form.schedule_duration),
        timezone: form.schedule_timezone,
      },
      settings: {
        max_participants: Number(form.max_participants),
        mode: form.mode,
        allow_chat: form.allow_chat,
        allow_reactions: form.allow_reactions,
        allow_hand_raise: form.allow_hand_raise,
        allow_qna: form.allow_qna,
        allow_polls: form.allow_polls,
        allow_screen_share: form.allow_screen_share,
        allow_whiteboard: form.allow_whiteboard,
        enable_recording: form.enable_recording,
        enable_breakout_rooms: form.enable_breakout_rooms,
        require_approval: form.require_approval,
        waiting_room: form.waiting_room,
      },
      course_id: form.course_id || null,
    };

    try {
      setSaving(true);
      if (isEdit) {
        await webinarAPI.update(webinarId, payload);
        toast.success('Webinaire mis à jour');
      } else {
        const { data } = await webinarAPI.create(payload);
        toast.success('Webinaire créé');
        navigate(`/admin/webinars/${data.id}/edit`, { replace: true });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    await handleSave();
    try {
      await webinarAPI.updateStatus(webinarId || '', 'scheduled');
      toast.success('Webinaire publié');
      navigate(`/admin/webinars`);
    } catch { toast.error('Erreur de publication'); }
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  return (
    <AppLayout>
    <div className="min-h-screen">
      {/* Top bar */}
      <div className="border-b border-border bg-background/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/admin/webinars`)}
              className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Video className="w-5 h-5 text-cyan-500" />
              <h1 className="font-semibold text-foreground">{isEdit ? 'Modifier le webinaire' : 'Nouveau webinaire'}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleSave} disabled={saving}
              className="border-input text-foreground hover:bg-muted gap-2">
              <Save className="w-4 h-4" /> Enregistrer
            </Button>
            {isEdit && (
              <Button onClick={handlePublish} disabled={saving}
                className="bg-gradient-to-r from-cyan-600 to-violet-600 text-white gap-2">
                <Eye className="w-4 h-4" /> Publier
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Basic info */}
        <section className="bg-card rounded-xl border border-border p-6 shadow-sm space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2 text-foreground">
            <Video className="w-5 h-5 text-cyan-500" /> Informations générales
          </h2>
          <div className="grid gap-4">
            <div>
              <Label className="text-muted-foreground">Titre *</Label>
              <Input value={form.title} onChange={(e) => update('title', e.target.value)}
                placeholder="ex: Introduction à Kubernetes" className="bg-background border-input mt-1.5" />
            </div>
            <div>
              <Label className="text-muted-foreground">Description</Label>
              <Textarea value={form.description} onChange={(e) => update('description', e.target.value)}
                placeholder="Décrivez votre webinaire..." rows={4} className="bg-background border-input mt-1.5" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground">Catégorie</Label>
                <Input value={form.category} onChange={(e) => update('category', e.target.value)}
                  placeholder="DevOps, Cloud, CI/CD..." className="bg-background border-input mt-1.5" />
              </div>
              <div>
                <Label className="text-muted-foreground">Tags (séparés par des virgules)</Label>
                <Input value={form.tags} onChange={(e) => update('tags', e.target.value)}
                  placeholder="kubernetes, docker, devops" className="bg-background border-input mt-1.5" />
              </div>
            </div>
          </div>
        </section>

        {/* Schedule */}
        <section className="bg-card rounded-xl border border-border p-6 shadow-sm space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2 text-foreground">
            <Calendar className="w-5 h-5 text-blue-500" /> Planification
          </h2>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-muted-foreground">Date et heure *</Label>
              <Input type="datetime-local" value={form.schedule_start}
                onChange={(e) => update('schedule_start', e.target.value)}
                className="bg-background border-input mt-1.5" />
            </div>
            <div>
              <Label className="text-muted-foreground">Durée (minutes)</Label>
              <Input type="number" value={form.schedule_duration}
                onChange={(e) => update('schedule_duration', e.target.value)}
                min={15} max={480} className="bg-background border-input mt-1.5" />
            </div>
            <div>
              <Label className="text-muted-foreground">Fuseau horaire</Label>
              <Select value={form.schedule_timezone} onValueChange={(v) => update('schedule_timezone', v)}>
                <SelectTrigger className="bg-background border-input mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Europe/Paris">Europe/Paris</SelectItem>
                  <SelectItem value="Europe/London">Europe/London</SelectItem>
                  <SelectItem value="America/New_York">America/New_York</SelectItem>
                  <SelectItem value="Asia/Tokyo">Asia/Tokyo</SelectItem>
                  <SelectItem value="UTC">UTC</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        {/* Visibility & Pricing */}
        <section className="bg-card rounded-xl border border-border p-6 shadow-sm space-y-4">
          <h2 className="text-lg font-semibold flex items-center gap-2 text-foreground">
            <Globe className="w-5 h-5 text-emerald-500" /> Visibilité & Tarification
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground">Visibilité</Label>
              <Select value={form.visibility} onValueChange={(v) => update('visibility', v)}>
                <SelectTrigger className="bg-background border-input mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public"><Globe className="w-3.5 h-3.5 inline mr-1.5" />Public</SelectItem>
                  <SelectItem value="private"><Lock className="w-3.5 h-3.5 inline mr-1.5" />Privé</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-muted-foreground">Tarification</Label>
              <Select value={form.pricing_type} onValueChange={(v) => update('pricing_type', v)}>
                <SelectTrigger className="bg-background border-input mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Gratuit</SelectItem>
                  <SelectItem value="paid"><DollarSign className="w-3.5 h-3.5 inline mr-1.5" />Payant</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.pricing_type === 'paid' && (
            <div className="max-w-xs">
              <Label className="text-muted-foreground">Prix (EUR)</Label>
              <Input type="number" value={form.pricing_price}
                onChange={(e) => update('pricing_price', e.target.value)}
                min={0} step={0.01} className="bg-background border-input mt-1.5" />
            </div>
          )}
        </section>

        {/* Advanced settings */}
        <Accordion type="single" collapsible className="bg-card rounded-xl border border-border p-2 shadow-sm">
          <AccordionItem value="settings" className="border-none">
            <AccordionTrigger className="text-lg font-semibold hover:no-underline px-4">
              <span className="flex items-center gap-2 text-foreground">
                <Settings2 className="w-5 h-5 text-violet-500" /> Paramètres avancés
              </span>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <div className="space-y-6 pt-2">
                <div className="max-w-xs">
                  <Label className="text-muted-foreground">Participants max</Label>
                  <Input type="number" value={form.max_participants}
                    onChange={(e) => update('max_participants', e.target.value)}
                    min={2} max={1000} className="bg-background border-input mt-1.5" />
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                  {[
                    ['allow_chat', 'Chat en direct'],
                    ['allow_reactions', 'Réactions emoji'],
                    ['allow_hand_raise', 'Lever la main'],
                    ['allow_qna', 'Questions/Réponses'],
                    ['allow_polls', 'Sondages'],
                    ['allow_screen_share', 'Partage d\'écran'],
                    ['allow_whiteboard', 'Tableau blanc'],
                    ['enable_recording', 'Enregistrement'],
                    ['enable_breakout_rooms', 'Breakout rooms'],
                    ['require_approval', 'Approbation requise'],
                    ['waiting_room', 'Salle d\'attente'],
                  ].map(([key, label]) => (
                    <div key={key} className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/20">
                      <Label className="text-sm text-foreground cursor-pointer" onClick={() => update(key, !form[key])}>{label}</Label>
                      <Switch checked={form[key]} onCheckedChange={(v) => update(key, v)} />
                    </div>
                  ))}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
    </AppLayout>
  );
}
